import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function App() {
  const [nickname, setNickname] = useState('');
  const [userId, setUserId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [matchStatus, setMatchStatus] = useState('idle'); // idle, queued, matched, ended
  const [seatNumber, setSeatNumber] = useState(null);
  const [opponentNickname, setOpponentNickname] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState('');
  const [score, setScore] = useState(0);
  const [overallStatus, setOverallStatus] = useState(null);

  const API_BASE_URL = 'https://pkm.oshijiku.com/wp-json/matching-system/v1'; // バトルサーチャーのWordPressサイトURL

  // Overall status polling
  useEffect(() => {
    const fetchOverallStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/status/overall`);
        const data = await response.json();
        if (response.ok) {
          setOverallStatus(data);
        } else {
          console.error('Error fetching overall status:', data.message);
        }
      } catch (error) {
        console.error('Network error fetching overall status:', error.message);
      }
    };

    fetchOverallStatus();
    const interval = setInterval(fetchOverallStatus, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, []);

  // Register user (nickname)
  const handleRegister = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nickname }),
      });
      const data = await response.json();
      if (response.ok) {
        setUserId(data.user_id);
        // setSessionId(data.session_id); // This session_id is for anonymous user tracking, not match session
        setMessage(`Registered as ${data.nickname}. User ID: ${data.user_id}`);
      } else {
        setMessage(`Error: ${data.message}`);
      }
    } catch (error) {
      setMessage(`Network error: ${error.message}`);
    }
  };

  // Start match
  const handleStartMatch = async () => {
    if (!userId) {
      setMessage('Please register a nickname first.');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, event_id: 'default_event' }), // Using a default event_id for v0
      });
      const data = await response.json();
      if (response.ok) {
        setMatchStatus(data.status);
        if (data.status === 'matched') {
          setSeatNumber(data.seat_number);
          setSessionId(data.session_id);
          setOpponentNickname(data.opponent_nickname);
          setMessage(`Matched! Your seat number is ${data.seat_number}. Opponent: ${data.opponent_nickname}`);
        } else if (data.status === 'queued') {
          setMessage('You are in the queue. Waiting for a match...');
        }
      } else {
        setMessage(`Error: ${data.message}`);
      }
    } catch (error) {
      setMessage(`Network error: ${error.message}`);
    }
  };

  // Poll for match status
  useEffect(() => {
    let interval;
    if (userId && (matchStatus === 'queued' || matchStatus === 'idle')) {
      interval = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/status?user_id=${userId}`);
          const data = await response.json();
          if (response.ok) {
            if (data.status === 'matched') {
              setMatchStatus(data.status);
              setSeatNumber(data.seat_number);
              setSessionId(data.session_id);
              setOpponentNickname(data.opponent_nickname);
              setMessage(`Matched! Your seat number is ${data.seat_number}. Opponent: ${data.opponent_nickname}`);
            } else if (data.status === 'idle') {
              // User might have been removed from queue or session ended by admin
              setMatchStatus('idle');
              setSeatNumber(null);
              setSessionId(null);
              setOpponentNickname('');
              setMessage('You are no longer in the queue or your session has ended.');
            }
          } else {
            setMessage(`Error checking status: ${data.message}`);
          }
        } catch (error) {
          setMessage(`Network error checking status: ${error.message}`);
        }
      }, 5000); // Poll every 5 seconds
    }
    return () => clearInterval(interval);
  }, [userId, matchStatus]);

  // Submit result
  const handleSubmitResult = async () => {
    if (!sessionId || !userId || !result) {
      setMessage('Please select a result and ensure you are in a session.');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/submit-result`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId, user_id: userId, result, score }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
        // After submitting result, allow re-matching
        setMatchStatus('ended'); // Mark session as ended for this user
        setSeatNumber(null);
        setSessionId(null);
        setOpponentNickname('');
        setResult('');
        setScore(0);
      } else {
        setMessage(`Error: ${data.message}`);
      }
    } catch (error) {
      setMessage(`Network error: ${error.message}`);
    }
  };

  const handleRematch = () => {
    setMatchStatus('idle');
    setMessage('Ready for a new match!');
  };

  return (
    <div className="App container mt-5">
      <h1 className="mb-4">バトルサーチャー (v0)</h1>

      {overallStatus && (
        <div className="card p-4 mb-4 bg-light">
          <h2 className="mb-3">現在のマッチング状況</h2>
          <p>最大同時対戦数: <strong>{overallStatus.max_seats}</strong></p>
          <p>利用可能な座席数: <strong>{overallStatus.available_seats}</strong></p>

          <h3 className="mt-4">アクティブな対戦 ({overallStatus.active_sessions_count})</h3>
          <div className="d-flex flex-wrap justify-content-center">
            {overallStatus.active_sessions.length > 0 ? (
              overallStatus.active_sessions.map((session) => (
                <div key={session.id} className="match-table card m-2 p-3">
                  <p>座席番号: <strong>{session.seat_number}</strong></p>
                  <div className="d-flex justify-content-around align-items-center">
                    <span className="player-name">{session.user1_nickname}</span>
                    <span className="vs">VS</span>
                    <span className="player-name">{session.user2_nickname}</span>
                  </div>
                </div>
              ))
            ) : (
              <p>現在、アクティブな対戦はありません。</p>
            )}
          </div>

          <h3 className="mt-4">マッチ待機中のユーザー ({overallStatus.queued_users_count})</h3>
          <div className="d-flex flex-wrap justify-content-center">
            {overallStatus.queued_users.length > 0 ? (
              overallStatus.queued_users.map((user) => (
                <span key={user.user_id} className="badge bg-secondary m-1">{user.nickname}</span>
              ))
            ) : (
              <p>現在、マッチ待機中のユーザーはいません。</p>
            )}
          </div>
        </div>
      )}

      <div className="card p-4 mb-4">
        <h2 className="mb-3">1. ニックネーム登録</h2>
        <div className="input-group mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="ニックネームを入力してください"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            disabled={userId !== null}
          />
          <button className="btn btn-primary" onClick={handleRegister} disabled={!nickname || userId !== null}>
            登録
          </button>
        </div>
        {userId && <p>ログイン中: <strong>{nickname}</strong> (ユーザーID: {userId})</p>}
      </div>

      <div className="card p-4 mb-4">
        <h2 className="mb-3">2. マッチ開始</h2>
        <button
          className="btn btn-success btn-lg"
          onClick={handleStartMatch}
          disabled={!userId || matchStatus === 'queued' || matchStatus === 'matched'}
        >
          {matchStatus === 'queued' ? 'マッチ待機中...' : 'マッチ開始'}
        </button>
        {matchStatus === 'queued' && <p className="mt-3">マッチ待機中です。しばらくお待ちください...</p>}
        {matchStatus === 'matched' && (
          <div className="mt-3 alert alert-info">
            <h3>マッチ成立！</h3>
            <p>あなたの座席番号: <strong>{seatNumber}</strong></p>
            <p>対戦相手: <strong>{opponentNickname}</strong></p>
            <p>セッションID: <strong>{sessionId}</strong></p>
          </div>
        )}
      </div>

      {matchStatus === 'matched' && (
        <div className="card p-4 mb-4">
          <h2 className="mb-3">3. 結果入力</h2>
          <div className="mb-3">
            <label htmlFor="resultSelect" className="form-label">勝敗:</label>
            <select id="resultSelect" className="form-select" value={result} onChange={(e) => setResult(e.target.value)}>
              <option value="">選択してください</option>
              <option value="win">勝ち</option>
              <option value="lose">負け</option>
              <option value="draw">引き分け</option>
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="scoreInput" className="form-label">スコア:</label>
            <input
              type="number"
              id="scoreInput"
              className="form-control"
              value={score}
              onChange={(e) => setScore(parseInt(e.target.value) || 0)}
            />
          </div>
          <button className="btn btn-primary" onClick={handleSubmitResult} disabled={!result || !sessionId}>
            結果を送信
          </button>
        </div>
      )}

      {matchStatus === 'ended' && (
        <div className="card p-4 mb-4">
          <h2 className="mb-3">対戦終了</h2>
          <p>対戦が終了しました。再度マッチを開始できます。</p>
          <button className="btn btn-info" onClick={handleRematch}>
            再度マッチ開始
          </button>
        </div>
      )}

      {message && <div className="alert alert-info mt-4">{message}</div>}
    </div>
  );
}

export default App;